import {
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CompanyService from "../../Services/Company.service";
import {
  IOnboardingSiteRequirements,
  IOnboardingWizardState,
} from "../../Interfaces/IOnboardingWizardState";
import RichTextEditor from "src/Shared/Components/RichTextEditor/RichTextEditor";
import {
  capOccfNotes,
  OCCF_NOTES_MAX_LENGTH,
  step6ValidationMessage,
} from "./OnboardingCapture.utils";
import { emDashWhenBlank } from "./OnboardingWizard.utils";
import { useWriteThrough, WriteThroughResult } from "./useWriteThrough";

type Props = {
  state: IOnboardingWizardState;
  companyRowID: string;
  frozen: boolean;
  registerFlush: (flush: () => Promise<WriteThroughResult>) => void;
};

/**
 * Wizard step 6 — Site Requirements and the information slots (stories 5612, 5613).
 *
 * The questions are driven by the server's reference list, so adding one there adds it to this
 * screen, the validation and the review together, and the retired MFA question cannot come back.
 * Neither radio is preselected: unanswered is distinct from No, and answering No completes a
 * question.
 */
const Step6SiteRequirements: React.FC<Props> = ({
  state,
  companyRowID,
  frozen,
  registerFlush,
}) => {
  const [siteRequirements, setSiteRequirements] = useState<IOnboardingSiteRequirements>({
    answers: { ...(state.siteRequirements?.answers || {}) },
    notes: state.siteRequirements?.notes ?? null,
  });
  const [bookingDetails, setBookingDetails] = useState<string>(
    state.information?.bookingDetails || "",
  );
  const [occfNotes, setOccfNotes] = useState<string>(state.information?.occfNotes || "");

  const writer = useWriteThrough<{
    siteRequirements: IOnboardingSiteRequirements;
    bookingDetails: string;
    occfNotes: string;
  }>((patch) =>
    CompanyService.saveOnboardingDraft({
      companyRowID,
      siteRequirements: patch.siteRequirements ?? siteRequirements,
      information: {
        financeDetails: state.information?.financeDetails ?? null,
        bookingDetails: patch.bookingDetails ?? bookingDetails,
        occfNotes: patch.occfNotes ?? occfNotes,
      },
    }),
  );

  useEffect(() => {
    registerFlush(() => writer.flush());
  }, [registerFlush, writer]);

  const save = (changes: Partial<{
    siteRequirements: IOnboardingSiteRequirements;
    bookingDetails: string;
    occfNotes: string;
  }>) => {
    if (frozen) return;
    writer.schedule({
      siteRequirements,
      bookingDetails,
      occfNotes,
      ...changes,
    } as any);
  };

  const answer = (key: string, value: boolean) => {
    const next = { ...siteRequirements, answers: { ...siteRequirements.answers, [key]: value } };
    setSiteRequirements(next);
    save({ siteRequirements: next });
  };

  const rtp = state.contacts.find((x) => x.isRequestingTestPersonnel);
  const outstanding = step6ValidationMessage({ ...state, siteRequirements } as any);

  return (
    <>
      <Typography variant="subtitle2" fontWeight={600}>
        Site requirements
      </Typography>

      {state.siteQuestions.map((question) => {
        const value = siteRequirements.answers?.[question.key];
        return (
          <Box
            key={question.key}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              py: 0.5,
            }}
          >
            <Typography variant="body2">{question.label}</Typography>
            <RadioGroup
              row
              value={value === true ? "yes" : value === false ? "no" : ""}
              onChange={(e) => answer(question.key, e.target.value === "yes")}
            >
              <FormControlLabel
                value="yes"
                control={<Radio size="small" disabled={frozen} />}
                label="Yes"
              />
              <FormControlLabel
                value="no"
                control={<Radio size="small" disabled={frozen} />}
                label="No"
              />
            </RadioGroup>
          </Box>
        );
      })}

      {outstanding && (
        <Typography variant="caption" color="warning.main">
          {outstanding}
        </Typography>
      )}

      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        label="Site Requirement Notes"
        disabled={frozen}
        sx={{ mt: 2 }}
        value={siteRequirements.notes || ""}
        onChange={(e) => {
          const next = { ...siteRequirements, notes: e.target.value };
          setSiteRequirements(next);
          save({ siteRequirements: next });
        }}
      />

      <Divider sx={{ my: 2 }} />

      {/* No certificate number: the field is withdrawn (task 6010). */}
      <Typography variant="subtitle2" fontWeight={600}>
        Report to Person (RTP)
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary" display="block">
            Name
          </Typography>
          <Typography variant="body2">{emDashWhenBlank(rtp?.fullName)}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary" display="block">
            Email
          </Typography>
          <Typography variant="body2">{emDashWhenBlank(rtp?.emailAddress)}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary" display="block">
            Phone
          </Typography>
          <Typography variant="body2">{emDashWhenBlank(rtp?.phoneNumber)}</Typography>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        Information
      </Typography>

      <RichTextEditor
        label="Booking Details"
        value={bookingDetails}
        disabled={frozen}
        helperText="Shown to the booking team before a booking is made."
        onChange={(html) => {
          setBookingDetails(html);
          save({ bookingDetails: html });
        }}
      />

      <Box sx={{ mt: 2 }}>
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          label="OCCF Notes"
          disabled={frozen}
          value={occfNotes}
          onChange={(e) => {
            const capped = capOccfNotes(e.target.value);
            setOccfNotes(capped);
            save({ occfNotes: capped });
          }}
          helperText={
            <Box component="span" sx={{ display: "flex", justifyContent: "space-between" }}>
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label="Internal — TDDA staff only, never client-visible"
              />
              <span>
                {occfNotes.length}/{OCCF_NOTES_MAX_LENGTH}
              </span>
            </Box>
          }
        />
      </Box>
    </>
  );
};

export default Step6SiteRequirements;
